import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  AppBar,
  Toolbar,
  CssBaseline,
} from "@mui/material";
import { margin, styled } from "@mui/system";
import {
  AutoAwesome,
  GridViewOutlined,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
  ReferenceLine,
  Cell,
  Rectangle,
  Line,
  Scatter,
  Customized,
} from "recharts";
import { SidebarFixed } from "./ProSidebar";
import Logo from "../../assets/Logo.svg";
import bgsd from "../../assets/bgsd.png";
import { FaChartLine } from "react-icons/fa";
import { useBenchmarks } from "./benchmarks-store";
import { LoadingGif } from "./LoadingGif";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsMore from 'highcharts/highcharts-more'; 





const MetacubeInsights = () => {
  const { categoryData, supplierData } = useBenchmarks(); // Assuming loading state is available
  const [expandedCompany, setExpandedCompany] = useState(true);
  const [expandedSupplier, setExpandedSupplier] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(true);
  const [loading, setLoading] = useState(true); // Local loading state

  

  console.log("categorybenchmarking_data is", categoryData);
  console.log("supplierbenchmarking_data is", supplierData);
  console.log(typeof supplierData);

  // Check if data is still loading
  const isDataLoading = !categoryData || !supplierData;

  let parsedSupplierData = [];
  if (supplierData) {
    if (typeof supplierData === "object") {
      // Already parsed
      parsedSupplierData = Array.isArray(supplierData)
        ? supplierData
        : [supplierData];
    } else if (typeof supplierData === "string") {
      // Only parse if it's a string
      try {
        const safeString = supplierData.replace(/\bNaN\b/g, "null");
        parsedSupplierData = JSON.parse(safeString);
      } catch (e) {
        console.error("Still failed to parse supplierData:", e);
      }
    }
  }

  // Helper functions to find data by benchmark name
  // Helper functions to find data by benchmark name
  const findCategoryData = (benchmarkName) => {
    if (!categoryData) return null;

    // ✅ Always look inside "result"
    const rawResult = categoryData.result;

    if (!rawResult) return null;

    // If already parsed as object/array
    if (typeof rawResult === "object") {
      const dataArray = Array.isArray(rawResult) ? rawResult : [rawResult];
      const item = dataArray.find(
        (item) => item.benchmark_name === benchmarkName
      );

      if (!item) return null;

      // Check if result is usable
      if (
        item.result === "N/A" ||
        (Array.isArray(item.result) && item.result.length === 0)
      ) {
        return null;
      }

      return item;
    }

    // If result came as string, parse safely
    if (typeof rawResult === "string") {
      try {
        const safeData = rawResult.replace(/\bNaN\b/g, "null");
        const parsed = JSON.parse(safeData);
        if (!Array.isArray(parsed)) return null;
        const item = parsed.find(
          (item) => item.benchmark_name === benchmarkName
        );

        if (!item) return null;

        if (
          item.result === "N/A" ||
          (Array.isArray(item.result) && item.result.length === 0)
        ) {
          return null;
        }

        return item;
      } catch (e) {
        console.error("Error parsing categoryData.result:", e);
        return null;
      }
    }

    return null;
  };

  const findSupplierData = (benchmarkName) => {
    if (!supplierData) return null;

    // Check if supplierData is already an array/object
    if (typeof supplierData === "object") {
      // If it's already parsed, use it directly
      const dataArray = Array.isArray(supplierData)
        ? supplierData
        : [supplierData];
      return dataArray.find((item) => item.benchmark_name === benchmarkName);
    }

    // Only try to parse if it's actually a string
    if (typeof supplierData === "string") {
      try {
        const safeData = supplierData.replace(/\bNaN\b/g, "null");
        const parsed = JSON.parse(safeData);
        if (!Array.isArray(parsed)) return null;
        return parsed.find((item) => item.benchmark_name === benchmarkName);
      } catch (e) {
        console.error("Error parsing supplierData:", e);
        return null;
      }
    }

    return null;
  };

  const Title = styled(Typography)(({ theme }) => ({
    fontSize: "14px",
    fontWeight: "600",
    color: "white",
    marginBottom: "8px",
  }));

  const SummaryBox = styled(Box)(({ theme }) => ({
    backgroundColor: "#FFFFFF0A",
    height: "auto",
    padding: "12px",
    fontSize: "11px",
    color: "#cfcfcf",
    marginTop: "8px",
    borderRadius: "6px",
    marginLeft: "24px",
  }));

  const ChartBox = styled(Box)(({ height }) => ({
    height: "500px",
    borderRadius: "4px",
    marginBottom: "8px",
    position: "relative",
    padding: "8px",
    marginLeft: "24px",
  }));

  const LoadingContainer = styled(Box)(() => ({
    height: "500px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    marginBottom: "8px",
    marginLeft: "24px",
  }));

  const NoDataContainer = styled(Box)(() => ({
    height: "500px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    marginBottom: "8px",
    marginLeft: "24px",
    color: "#cfcfcf",
    fontSize: "16px",
    fontStyle: "italic",
  }));

  const Section = ({ title, children }) => (
    <Box sx={{ marginBottom: 6 }}>
      <Typography
        sx={{ color: "#cfcfcf", fontSize: "13px", marginBottom: "12px" }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );

  // Loading Component
  const LoadingChart = () => (
    <LoadingContainer>
      <LoadingGif />
    </LoadingContainer>
  );

  // No Data Component
  const NoDataChart = () => (
    <NoDataContainer>No benchmark data available</NoDataContainer>
  );

  // Chart Wrapper Component
  const ChartWrapper = ({
    benchmarkName,
    categorySpend,
    chartComponent,
    showRecommendations = true,
  }) => {
    if (isDataLoading) {
      return <LoadingChart />;
    }

    const dataItem =
      findCategoryData(benchmarkName) || findSupplierData(benchmarkName);
    const hasData = dataItem && dataItem.result;

    return (
      <>
        {hasData ? <ChartBox>{chartComponent}</ChartBox> : <NoDataChart />}

        {hasData && showRecommendations && (
          <SummaryBox>
            <Box display="flex" alignItems="center" mb={1}>
              <AutoAwesome
                sx={{ fontSize: 16, marginRight: "4px", color: "white" }}
              />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#ffffff",
                  fontStyle: "Roboto",
                }}
              >
                Recommendations
              </Typography>
            </Box>
            <Typography
              sx={{
                fontWeight: 400,
                fontSize: "14px",
                fontStyle: "Roboto",
                color: "#FFFFFF99",
              }}
            >
              {dataItem?.insight_text?.[0] || "No recommendations available"}
            </Typography>
          </SummaryBox>
        )}
      </>
    );
  };

  const formatSpend = (amount) => {
    if (amount >= 1e9) {
      return `${(amount / 1e9).toFixed(2)} Bn`;
    } else if (amount >= 1e6) {
      return `${(amount / 1e6).toFixed(2)} Mn`;
    } else if (amount >= 1e3) {
      return `${(amount / 1e3).toFixed(2)} K`;
    } else {
      return `${amount.toFixed(2)}`;
    }
  };

  const currencySymbols = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    AUD: "A$",
  };

  // Updated BenchmarkChart component
  // const BenchmarkChart = ({ benchmarkName }) => {
  //   const dataItem =
  //     findCategoryData(benchmarkName) || findSupplierData(benchmarkName);

  //   // Use optional chaining to safely check nested properties
  //   if (!dataItem?.result?.metacube || !dataItem?.result?.spendcube) {
  //     return null; // This will be handled by ChartWrapper
  //   }

  //   const getCurrencySymbol = (currency) =>
  //     currencySymbols[currency] || currency;

  //   // ✅ Extract spend + currency from categoryData
  //   const spendValue = categoryData?.spend?.value || 0;
  //   const spendCurrency = categoryData?.spend?.currency || "USD";

  //   const categorySpend = `${getCurrencySymbol(spendCurrency)}${formatSpend(
  //     spendValue
  //   )}`;

  //   const data = {
  //     benchmarks: {
  //       q1: dataItem.result.metacube.q1 || dataItem.result.metacube[" q1"],
  //       q3: dataItem.result.metacube.q3 || dataItem.result.metacube[" q3"],
  //       min: dataItem.result.metacube.min,
  //       max: dataItem.result.metacube.max,
  //       median: dataItem.result.metacube.median,
  //       avg: dataItem.result.metacube.avg,
  //     },
  //     metric: dataItem.result.spendcube.metric,
  //     commentary: dataItem.insight_text[0],
  //   };

  //   // Set proper range based on client value position
  //   const clientValue = data.metric;
  //   const q1 = data.benchmarks.q1;
  //   const q3 = data.benchmarks.q3;

  //   let minValue, maxValue;

  //   if (clientValue >= q1 && clientValue <= q3) {
  //     // Client value is within Q1-Q3 range
  //     minValue = q1;
  //     maxValue = q3;
  //   } else if (clientValue > q3) {
  //     // Client value is above Q3 - extend Y-axis to show client value
  //     minValue = q1;
  //     maxValue = clientValue;
  //   } else {
  //     // Client value is below Q1 - extend Y-axis to show client value
  //     minValue = clientValue;
  //     maxValue = q3;
  //   }

  //   // Only keep Q1–Q3 range
  //   const chartData = [
  //     {
  //       name: "Benchmark Range", // category label
  //       q1ToQ3Range: q3, // Bar always goes to Q3 (represents Q1-Q3 range)
  //       clientValue: data.metric,
  //     },
  //   ];

  //   // Determine chart title based on benchmark name
  //   const getChartTitle = () => {
  //     switch (benchmarkName) {
  //       case "Category Spend By Total Spend":
  //         return "Category Spend as % of Total Spend";
  //       case "Category Spend By Revenue":
  //         return "Category Spend as % of Revenue";
  //       case "Client Spend By Revenue":
  //         return "Total Spend as % of Revenue";
  //       case "Supplier Pareto":
  //         return "Top 10 Suppliers Contribution (%)";
  //       case "Top 80% Suppliers By Category":
  //         return "Number of Suppliers (Top 80%)";
  //       case "Bottom 20% Suppliers By Category":
  //         return "Number of Suppliers (Bottom 20%)";
  //       case "Spend by Supplier for top 80% Suppliers":
  //         return "Average Spend per Supplier ($)";
  //       case "Spend by Supplier for bottom 20% Suppliers":
  //         return "Average Spend per Supplier ($)";
  //       case "Supplier Wallet Share":
  //         return "Wallet Share (%)";
  //       default:
  //         return "Value";
  //     }
  //   };

  //   const formatYAxisTick = (value) => {
  //     // Handle undefined, null, or non-numeric values
  //     if (value === undefined || value === null || isNaN(value)) {
  //       return "0";
  //     }

  //     // Convert to number if it's a string
  //     const numValue = typeof value === "string" ? parseFloat(value) : value;

  //     if (isNaN(numValue)) {
  //       return "0";
  //     }

  //     if (benchmarkName.includes("Spend by Supplier")) {
  //       return `$${(numValue / 1000000).toFixed(1)}M`;
  //     } else if (
  //       benchmarkName.includes("Top 80% Total Suppliers") ||
  //       benchmarkName.includes("Top 80% Suppliers By Category") ||
  //       benchmarkName.includes("Bottom 20% Suppliers By Category")
  //     ) {
  //       return `${numValue}`;
  //     }
  //     return `${numValue.toFixed(1)}%`;
  //   };

  //   return (
  //     <Box sx={{ height: "100%", position: "relative" }}>
  //       <Typography sx={{ color: "#888", fontSize: "12px", mb: 1 }}>
  //         Category Spend: {categorySpend}
  //       </Typography>

  //       <ResponsiveContainer width="100%" height="80%">
  //         <ComposedChart
  //           data={chartData}
  //           margin={{ top: 20, right: 30, left: 60, bottom: 60 }}
  //         >
  //           <CartesianGrid
  //             strokeDasharray="3 3"
  //             stroke="#333"
  //             horizontal={true}
  //             vertical={false}
  //           />
  //           <YAxis
  //             type="number"
  //             domain={[minValue, maxValue]} // Start at Q1, end at Q3
  //             tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //             tickFormatter={formatYAxisTick}
  //             label={{
  //               value: getChartTitle(),
  //               angle: -90,
  //               offset: -5,
  //               position: "insideLeft",
  //               style: {
  //                 textAnchor: "middle",
  //                 fill: "#cfcfcf",
  //                 fontSize: "11px",
  //               },
  //             }}
  //             tickCount={6}
  //           />
  //           <XAxis
  //             dataKey="null"
  //             type="category"
  //             tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //             label={{
  //               value: "Benchmark Range",
  //               position: "insideBottom",
  //               offset: -5,
  //               style: {
  //                 textAnchor: "middle",
  //                 fill: "#cfcfcf",
  //                 fontSize: "11px",
  //               },
  //             }}
  //           />

  //           <Bar dataKey="q1ToQ3Range" fill="#F368CC29" barSize={40} />

  //           {/* Client value marker */}
  //           <Customized
  //             component={({ yAxisMap, height, width }) => {
  //               const yScale = yAxisMap[Object.keys(yAxisMap)[0]].scale;
  //               const y = yScale(data.metric);
  //               const barWidth = 40;
  //               const centerX = width / 2;

  //               return (
  //                 <line
  //                   x1={centerX + 27}
  //                   x2={centerX + barWidth + 23}
  //                   y1={y}
  //                   y2={y}
  //                   stroke="#F368CC"
  //                   strokeWidth={4}
  //                   strokeLinecap="round"
  //                 />
  //               );
  //             }}
  //           />

  //           <Tooltip
  //             content={({ active }) => {
  //               if (active) {
  //                 return (
  //                   <div
  //                     style={{
  //                       backgroundColor: "#FFFFFF0F",
  //                       padding: "10px",
  //                       borderRadius: "6px",
  //                       color: "white",
  //                     }}
  //                   >
  //                     <p
  //                       style={{
  //                         color: "white",
  //                         fontSize: "12px",
  //                         margin: "2px 0",
  //                       }}
  //                     >
  //                       Client Value:{" "}
  //                       {data.metric !== undefined && data.metric !== null
  //                         ? formatYAxisTick(data.metric)
  //                         : "N/A"}
  //                     </p>
  //                     <p
  //                       style={{
  //                         color: "white",
  //                         fontSize: "12px",
  //                         margin: "2px 0",
  //                       }}
  //                     >
  //                       Q1 (25th percentile):{" "}
  //                       {data.benchmarks.q1 !== undefined &&
  //                       data.benchmarks.q1 !== null
  //                         ? formatYAxisTick(data.benchmarks.q1)
  //                         : "N/A"}
  //                     </p>
  //                     <p
  //                       style={{
  //                         color: "white",
  //                         fontSize: "12px",
  //                         margin: "2px 0",
  //                       }}
  //                     >
  //                       Median:{" "}
  //                       {data.benchmarks.median !== undefined &&
  //                       data.benchmarks.median !== null
  //                         ? formatYAxisTick(data.benchmarks.median)
  //                         : "N/A"}
  //                     </p>
  //                     <p
  //                       style={{
  //                         color: "white",
  //                         fontSize: "12px",
  //                         margin: "2px 0",
  //                       }}
  //                     >
  //                       Q3 (75th percentile):{" "}
  //                       {data.benchmarks.q3 !== undefined &&
  //                       data.benchmarks.q3 !== null
  //                         ? formatYAxisTick(data.benchmarks.q3)
  //                         : "N/A"}
  //                     </p>
  //                     <p
  //                       style={{
  //                         color: "white",
  //                         fontSize: "12px",
  //                         margin: "2px 0",
  //                       }}
  //                     >
  //                       Average:{" "}
  //                       {data.benchmarks.avg !== undefined &&
  //                       data.benchmarks.avg !== null
  //                         ? formatYAxisTick(data.benchmarks.avg)
  //                         : "N/A"}
  //                     </p>
  //                   </div>
  //                 );
  //               }
  //               return null;
  //             }}
  //             cursor={false}
  //           />
  //         </ComposedChart>
  //       </ResponsiveContainer>

  //       {/* Legend */}
  //       <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
  //         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
  //           <Box
  //             sx={{
  //               width: 12,
  //               height: 12,
  //               bgcolor: "#F368CC29",
  //               borderRadius: 1,
  //             }}
  //           />
  //           <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
  //             Benchmark Range (Q1-Q3)
  //           </Typography>
  //         </Box>
  //         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
  //           <Box
  //             sx={{
  //               width: 12,
  //               height: 12,
  //               bgcolor: "#F368CC",
  //               borderRadius: 1,
  //             }}
  //           />
  //           <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
  //             Client Value
  //           </Typography>
  //         </Box>
  //       </Box>
  //     </Box>
  //   );
  // };

  // // Updated SupplierPrevalenceChart component
  // const SupplierPrevalenceChart = () => {
  //   const [chartDimensions, setChartDimensions] = useState({
  //     width: 0,
  //     height: 0,
  //   });
  //   const chartRef = useRef(null);

  //   useEffect(() => {
  //     const updateDimensions = () => {
  //       if (chartRef.current) {
  //         const rect = chartRef.current.getBoundingClientRect();
  //         setChartDimensions({ width: rect.width, height: rect.height });
  //       }
  //     };
  //     const timer = setTimeout(updateDimensions, 100);
  //     window.addEventListener("resize", updateDimensions);
  //     return () => {
  //       clearTimeout(timer);
  //       window.removeEventListener("resize", updateDimensions);
  //     };
  //   }, []);

  //   const dataItem = findSupplierData("Supplier Prevalence");
  //   if (!dataItem || !dataItem.result) return null; // This will be handled by ChartWrapper

  //   const processedData = dataItem.result.slice(0, 10).map((item) => ({
  //     name:
  //       item["Harmonized Supplier Name(M)"].length > 15
  //         ? item["Harmonized Supplier Name(M)"].substring(0, 15) + "..."
  //         : item["Harmonized Supplier Name(M)"],
  //     fullName: item["Harmonized Supplier Name(M)"],
  //     contribution: item["Contribution to L1 Category"],
  //     benchmarkMin: item.q1, // Q1
  //     benchmarkMax: item.q3, // Q3
  //   }));

  //   const minQ1 = Math.min(...processedData.map((d) => d.benchmarkMin));
  //   const maxQ3 = Math.max(...processedData.map((d) => d.benchmarkMax));

  //   // Build chart data: invisible offset up to Q1, then visible Q1→Q3 segment
  //   const chartData = processedData.map((d) => ({
  //     ...d,
  //     q1Offset: d.benchmarkMin, // invisible base
  //     q1ToQ3Range: d.benchmarkMax - d.benchmarkMin, // visible bar height
  //   }));

  //   // Helper to position contribution lines within the current Y domain (minQ1..maxQ3)
  //   const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
  //   const domainSpan = Math.max(1e-6, maxQ3 - minQ1);

  //   const ContributionLine = (props) => {
  //     const { x, y, width } = props;
  //     if (y == null || Number.isNaN(y)) return null;
  //     return (
  //       <g>
  //         <line
  //           x1={x - 62.5}
  //           x2={x - 5.5}
  //           y1={y}
  //           y2={y}
  //           stroke="#F368CC"
  //           strokeWidth={3}
  //           strokeLinecap="round"
  //         />
  //       </g>
  //     );
  //   };

  //   return (
  //     <Box sx={{ height: "100%", position: "relative" }} ref={chartRef}>
  //       <ResponsiveContainer width="100%" height="80%">
  //         <BarChart
  //           data={chartData}
  //           margin={{ top: 5, right: 30, left: 60, bottom: 80 }}
  //         >
  //           <CartesianGrid strokeDasharray="3 3" stroke="#333" />
  //           <XAxis
  //             dataKey="name"
  //             tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //             height={60}
  //             interval={0}
  //             textAnchor="middle"
  //             label={{
  //               value: "Supplier Name",
  //               position: "insideBottom",
  //               offset: -5,
  //               style: {
  //                 textAnchor: "middle",
  //                 fill: "#cfcfcf",
  //                 fontSize: "11px",
  //               },
  //             }}
  //           />
  //           <YAxis
  //             tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //             domain={[minQ1, maxQ3]} // domain matches Q1..Q3
  //             tickFormatter={(value) => `${value.toFixed(1)}%`}
  //             tickCount={8}
  //             label={{
  //               value: "% Contribution to Category Spend",
  //               angle: -90,
  //               position: "insideLeft",
  //               style: {
  //                 textAnchor: "middle",
  //                 fill: "#cfcfcf",
  //                 fontSize: "11px",
  //               },
  //             }}
  //           />

  //           {/* Invisible offset to start bars at Q1 */}
  //           <Bar
  //             dataKey="q1Offset"
  //             stackId="bench"
  //             fill="transparent"
  //             stroke="none"
  //             isAnimationActive={false}
  //             legendType="none"
  //             barSize={60}
  //           />

  //           {/* Visible Q1→Q3 range */}
  //           <Bar
  //             dataKey="q1ToQ3Range"
  //             stackId="bench"
  //             fill="#F368CC29"
  //             barSize={60}
  //           />

  //           <Bar
  //             dataKey="contribution"
  //             fill="transparent"
  //             stroke="none"
  //             legendType="none"
  //             shape={<ContributionLine />}
  //             isAnimationActive={false}
  //             barSize={60}
  //           />

  //           <Tooltip
  //             content={({ active, payload }) => {
  //               if (active && payload && payload.length) {
  //                 const data = payload[0].payload;
  //                 return (
  //                   <div
  //                     style={{
  //                       backgroundColor: "#FFFFFF0F",
  //                       padding: "10px",
  //                       borderRadius: "6px",
  //                       color: "white",
  //                     }}
  //                   >
  //                     <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                       {data.fullName}
  //                     </p>
  //                     <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                       Contribution: {data.contribution}%
  //                     </p>
  //                     <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                       Benchmark Range (Q1–Q3): {data.benchmarkMin}% –{" "}
  //                       {data.benchmarkMax}%
  //                     </p>
  //                   </div>
  //                 );
  //               }
  //               return null;
  //             }}
  //             cursor={{ fill: "transparent" }}
  //           />
  //         </BarChart>
  //       </ResponsiveContainer>

  //       {/* Legend */}
  //       <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
  //         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
  //           <Box
  //             sx={{
  //               width: 12,
  //               height: 12,
  //               bgcolor: "#F368CC29",
  //               borderRadius: 1,
  //             }}
  //           />
  //           <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
  //             Benchmark Range (Q1–Q3)
  //           </Typography>
  //         </Box>
  //         <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
  //           <Box
  //             sx={{
  //               width: 12,
  //               height: 12,
  //               bgcolor: "#F368CC",
  //               borderRadius: 1,
  //             }}
  //           />
  //           <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
  //             Actual Contribution
  //           </Typography>
  //         </Box>
  //       </Box>
  //     </Box>
  //   );
  // };

  // // Updated FragmentationChart component
  // const FragmentationChart = () => {
  //   const dataItem = findCategoryData("Category Fragmentation");
  //   if (!dataItem || !dataItem.result) return null; // This will be handled by ChartWrapper

  //   const chartDataRaw = dataItem.result
  //     .filter((item) => item["Sub Category Supplier Count"] !== null)
  //     .map((item) => ({
  //       name: item["Level 2"],
  //       count: parseInt(item["Sub Category Supplier Count"]),
  //       q1: item.q1,
  //       q3: item.q3,
  //       min: item.min,
  //       max: item.max,
  //       median: item.median,
  //       avg: item.avg,
  //     }));

  //   // Domain to include Q1–Q3 range and actual counts
  //   const minQ1 = Math.min(...chartDataRaw.map((d) => d.q1));
  //   const maxQ3 = Math.max(...chartDataRaw.map((d) => d.q3));
  //   const minVal = Math.min(
  //     ...chartDataRaw.map((d) => Math.min(d.min, d.count, d.q1))
  //   );
  //   const maxVal = Math.max(
  //     ...chartDataRaw.map((d) => Math.max(d.max, d.count, d.q3))
  //   );
  //   const domain = [minQ1, maxQ3];

  //   // Stacked data so bars start at Q1 and show only Q1→Q3 span
  //   const chartData = chartDataRaw.map((d) => ({
  //     ...d,
  //     q1Offset: d.q1,
  //     q1ToQ3Range: Math.max(0, d.q3 - d.q1),
  //   }));

  //   // Custom line shape for actual count
  //   const ActualLine = (props) => {
  //     const { x, y, width } = props;
  //     if (y == null || Number.isNaN(y)) return null;
  //     return (
  //       <line
  //         x1={x - 63.5}
  //         x2={x - 5}
  //         y1={y}
  //         y2={y}
  //         stroke="#F368CC"
  //         strokeWidth={3}
  //         strokeLinecap="round"
  //       />
  //     );
  //   };

  //   return (
  //     <ResponsiveContainer width="100%" height="100%">
  //       <BarChart
  //         data={chartData}
  //         margin={{ top: 20, right: 30, left: 60, bottom: 100 }}
  //       >
  //         <CartesianGrid strokeDasharray="3 3" stroke="#333" />
  //         <XAxis
  //           dataKey="name"
  //           tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //           textAnchor="middle"
  //           height={80}
  //           label={{
  //             value: "Sub-Category",
  //             position: "insideBottom",
  //             offset: -5,
  //             style: {
  //               textAnchor: "middle",
  //               fill: "#cfcfcf",
  //               fontSize: "11px",
  //             },
  //           }}
  //         />
  //         <YAxis
  //           tick={{ fill: "#cfcfcf", fontSize: 10 }}
  //           domain={domain}
  //           tickCount={8}
  //           label={{
  //             value: "Number of Suppliers",
  //             angle: -90,
  //             position: "insideLeft",
  //             style: {
  //               textAnchor: "middle",
  //               fill: "#cfcfcf",
  //               fontSize: "11px",
  //             },
  //           }}
  //         />
  //         <Tooltip
  //           content={({ active, payload }) => {
  //             if (active && payload && payload.length) {
  //               const d = payload[0].payload;
  //               return (
  //                 <div
  //                   style={{
  //                     backgroundColor: "#FFFFFF0F",
  //                     padding: "10px",
  //                     borderRadius: "6px",
  //                     color: "white",
  //                   }}
  //                 >
  //                   <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                     {d.name}: {d.count} suppliers
  //                   </p>
  //                   <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                     Benchmark Range (Q1–Q3): {d.q1} – {d.q3}
  //                   </p>
  //                   <p style={{ fontSize: "12px", margin: "2px 0" }}>
  //                     Median: {d.median}
  //                   </p>
  //                 </div>
  //               );
  //             }
  //             return null;
  //           }}
  //           cursor={{ fill: "transparent" }}
  //         />

  //         {/* Invisible offset so the visible bar starts at Q1 */}
  //         <Bar
  //           dataKey="q1Offset"
  //           stackId="bench"
  //           fill="transparent"
  //           stroke="none"
  //           isAnimationActive={false}
  //           barSize={60}
  //         />
  //         {/* Visible Q1→Q3 range */}
  //         <Bar
  //           dataKey="q1ToQ3Range"
  //           stackId="bench"
  //           fill="#F368CC29"
  //           barSize={60}
  //           radius={[2, 2, 0, 0]}
  //         />
  //         {/* Actual value as a horizontal line per bar */}
  //         <Bar
  //           dataKey="count"
  //           shape={<ActualLine />}
  //           isAnimationActive={false}
  //           fill="transparent"
  //           barSize={60}
  //         />
  //       </BarChart>
  //     </ResponsiveContainer>
  //   );
  // };


  // Updated BenchmarkChart component
const BenchmarkChart = ({ benchmarkName }) => {
  const dataItem = findCategoryData(benchmarkName) || findSupplierData(benchmarkName);

  if (!dataItem?.result?.metacube || !dataItem?.result?.spendcube) {
    return null;
  }

  const getCurrencySymbol = (currency) => currencySymbols[currency] || currency;

  const spendValue = categoryData?.spend?.value || 0;
  const spendCurrency = categoryData?.spend?.currency || "USD";
  const categorySpend = `${getCurrencySymbol(spendCurrency)}${formatSpend(spendValue)}`;

  const data = {
    benchmarks: {
      q1: dataItem.result.metacube.q1 || dataItem.result.metacube[" q1"],
      q3: dataItem.result.metacube.q3 || dataItem.result.metacube[" q3"],
      min: dataItem.result.metacube.min,
      max: dataItem.result.metacube.max,
      median: dataItem.result.metacube.median,
      avg: dataItem.result.metacube.avg,
    },
    metric: dataItem.result.spendcube.metric,
    commentary: dataItem.insight_text[0],
  };

  const clientValue = data.metric;
  const q1 = data.benchmarks.q1;
  const q3 = data.benchmarks.q3;

  let minValue, maxValue;
  if (clientValue >= q1 && clientValue <= q3) {
    minValue = q1;
    maxValue = q3;
  } else if (clientValue > q3) {
    minValue = q1;
    maxValue = clientValue;
  } else {
    minValue = clientValue;
    maxValue = q3;
  }


  
  const getChartTitle = () => {
    switch (benchmarkName) {
      case "Category Spend By Total Spend":
        return "Category Spend as % of Total Spend";
      case "Category Spend By Revenue":
        return "Category Spend as % of Revenue";
      case "Client Spend By Revenue":
        return "Total Spend as % of Revenue";
      case "Supplier Pareto":
        return "Top 10 Suppliers Contribution (%)";
      case "Top 80% Suppliers By Category":
        return "Number of Suppliers (Top 80%)";
      case "Bottom 20% Suppliers By Category":
        return "Number of Suppliers (Bottom 20%)";
      case "Spend by Supplier for top 80% Suppliers":
        return "Average Spend per Supplier ($)";
      case "Spend by Supplier for bottom 20% Suppliers":
        return "Average Spend per Supplier ($)";
      case "Supplier Wallet Share":
        return "Wallet Share (%)";
      default:
        return "Value";
    }
  };

  const formatValue = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "0";
    }

    const numValue = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(numValue)) return "0";

    if (benchmarkName.includes("Spend by Supplier")) {
      return `$${(numValue / 1000000).toFixed(1)}M`;
    } else if (
      benchmarkName.includes("Top 80% Total Suppliers") ||
      benchmarkName.includes("Top 80% Suppliers By Category") ||
      benchmarkName.includes("Bottom 20% Suppliers By Category")
    ) {
      return `${numValue}`;
    }
    return `${numValue.toFixed(1)}%`;
  };

  const options = {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      height: 400,
      style: {
        fontFamily: 'Arial, sans-serif'
      }
    },
    title: {
      text: null
    },
    xAxis: {
      categories: ['Benchmark Range'],
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '11px',
        }
      },
      title: {
        // text: 'Benchmark Range',
        style: {
          color: '#cfcfcf',
          fontSize: '11px'
        }
      },
      lineColor: '#333',
      tickColor: '#333'
    },
    yAxis: {
      min: minValue,
      max: maxValue,
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '10px'
        },
        formatter: function() {
          return formatValue(this.value);
        }
      },
      // labels: {
      //   enabled: false // Disable default labels
      // },
      title: {
        text: getChartTitle(),
        style: {
          color: '#cfcfcf',
          fontSize: '11px',
        },
        offset: 50 
      },
      gridLineColor: '#333',
      gridLineDashStyle: 'ShortDash'
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      bar: {
        groupPadding: 0.1,
        pointPadding: 0.2,
        borderWidth: 0
      },
      series: {
        animation: {
          duration: 1000
        }
      }
    },
    series: [{
      name: 'Q1-Q3 Range',
      data: [{
        low: q1,
        high: q3,
        color: 'rgba(243, 104, 204, 0.16)',
        borderColor: '#F368CC',   // purple border
      }],
      type: 'columnrange',
      grouping: false
    }, {
      name: 'Client Value',
      data: [{
        x: 0,
        y: clientValue,
        marker: {
          symbol: 'diamond',
          radius: 8,
          fillColor: '#F368CC',
          lineWidth: 2,
          lineColor: '#F368CC'
        }
      }],
      type: 'scatter',
      showInLegend: false
    }],
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'transparent',
      style: {
        color: 'white',
        fontSize: '12px'
      },
      formatter: function() {
        return `
          <div style="color: white;">
            <p>Client Value: ${formatValue(data.metric)}</p>
            <p>Q1 (25th percentile): ${formatValue(data.benchmarks.q1)}</p>
            <p>Median: ${formatValue(data.benchmarks.median)}</p>
            <p>Q3 (75th percentile): ${formatValue(data.benchmarks.q3)}</p>
            <p>Average: ${formatValue(data.benchmarks.avg)}</p>
          </div>
        `;
      },
      useHTML: true
    },
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          chart: {
            height: 300
          },
          yAxis: {
            labels: {
              style: {
                fontSize: '9px'
              }
            }
          }
        }
      }]
    }
  };

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      <Typography sx={{ color: "#888", fontSize: "12px", mb: 1 }}>
        Category Spend: {categorySpend}
      </Typography>

      <HighchartsReact
        highcharts={Highcharts}
        options={options}
      />

      {/* Legend */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: "#F368CC29",
              borderRadius: 1,
            }}
          />
          <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
            Benchmark Range (Q1-Q3)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: "#F368CC",
              borderRadius: 1,
              transform: 'rotate(45deg)'
            }}
          />
          <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
            Client Value
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Updated SupplierPrevalenceChart component
const SupplierPrevalenceChart = () => {
  const dataItem = findSupplierData("Supplier Prevalence");
  if (!dataItem || !dataItem.result) return null;

  const processedData = dataItem.result.slice(0, 10).map((item, index) => ({
    name: item["Harmonized Supplier Name(M)"].length > 15
      ? item["Harmonized Supplier Name(M)"].substring(0, 15) + "..."
      : item["Harmonized Supplier Name(M)"],
    fullName: item["Harmonized Supplier Name(M)"],
    contribution: item["Contribution to L1 Category"],
    benchmarkMin: item.q1,
    benchmarkMax: item.q3,
    index: index
  }));

  const categories = processedData.map(d => d.name);
  const rangeData = processedData.map(d => [d.benchmarkMin, d.benchmarkMax]);
  const contributionData = processedData.map((d, index) => ({
    x: index,
    y: d.contribution,
    fullName: d.fullName,
    benchmarkMin: d.benchmarkMin,
    benchmarkMax: d.benchmarkMax
  }));
  

  const options = {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      height: 500,
    },
    title: {
      text: null
    },
    xAxis: {
      categories: categories,
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '10px'
        }
      },
      title: {
        text: 'Supplier Name',
        style: {
          color: '#cfcfcf',
          fontSize: '11px'
        }
      },
      lineColor: '#333',
      tickColor: '#333'
    },
    yAxis: {
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '10px'
        },
        formatter: function() {
          return this.value.toFixed(1) + '%';
        }
      },
      title: {
        text: '% Contribution to Category Spend',
        style: {
          color: '#cfcfcf',
          fontSize: '11px'
        },
        offset: 50
      },
      gridLineColor: '#333',
      gridLineDashStyle: 'ShortDash'
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      bar: {
        groupPadding: 0.1,
        pointPadding: 0.2,
        borderWidth: 0
      }
    },
    series: [{
      name: 'Benchmark Range',
      data: rangeData,
      type: 'columnrange',
      color: 'rgba(243, 104, 204, 0.16)'
    }, {
      name: 'Actual Contribution',
      data: contributionData,
      type: 'scatter',
      marker: {
        symbol: 'diamond',
        radius: 6,
        fillColor: '#F368CC',
        lineWidth: 2,
        lineColor: '#F368CC'
      }
    }],
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'transparent',
      style: {
        color: 'white',
        fontSize: '12px'
      },
      formatter: function() {
        if (this.series.name === 'Actual Contribution') {
          return `
            <div style="color: white;">
              <p><b>${this.point.fullName}</b></p>
              <p>Contribution: ${this.point.y}%</p>
              <p>Benchmark Range (Q1–Q3): ${this.point.benchmarkMin}% – ${this.point.benchmarkMax}%</p>
            </div>
          `;
        }
        return false;
      },
      useHTML: true
    },
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          chart: {
            height: 400
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '9px'
              }
            }
          }
        }
      }]
    }
  };

  return (
    <Box sx={{ height: "100%", position: "relative" }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
      />

      {/* Legend */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: "#F368CC29",
              borderRadius: 1,
            }}
          />
          <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
            Benchmark Range (Q1–Q3)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              bgcolor: "#F368CC",
              borderRadius: 1,
              transform: 'rotate(45deg)'
            }}
          />
          <Typography sx={{ color: "#cfcfcf", fontSize: "10px" }}>
            Actual Contribution
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Updated FragmentationChart component
const FragmentationChart = () => {
  const dataItem = findCategoryData("Category Fragmentation");
  if (!dataItem || !dataItem.result) return null;

  const chartDataRaw = dataItem.result
    .filter((item) => item["Sub Category Supplier Count"] !== null)
    .map((item) => ({
      name: item["Level 2"],
      count: parseInt(item["Sub Category Supplier Count"]),
      q1: item.q1,
      q3: item.q3,
      min: item.min,
      max: item.max,
      median: item.median,
      avg: item.avg,
    }));

  const categories = chartDataRaw.map(d => d.name);
  const rangeData = chartDataRaw.map(d => [d.q1, d.q3]);
  const actualData = chartDataRaw.map((d, index) => ({
    x: index,
    y: d.count,
    q1: d.q1,
    q3: d.q3,
    median: d.median,
    name: d.name
  }));

  const options = {
    chart: {
      type: 'bar',
      backgroundColor: 'transparent',
      height: 400,
    },
    title: {
      text: null
    },
    xAxis: {
      categories: categories,
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '10px'
        }
      },
      title: {
        text: 'Sub-Category',
        style: {
          color: '#cfcfcf',
          fontSize: '11px'
        }
      },
      lineColor: '#333',
      tickColor: '#333'
    },
    yAxis: {
      labels: {
        style: {
          color: '#cfcfcf',
          fontSize: '10px'
        }
      },
      title: {
        text: 'Number of Suppliers',
        style: {
          color: '#cfcfcf',
          fontSize: '11px'
        }
      },
      gridLineColor: '#333',
      gridLineDashStyle: 'ShortDash'
    },
    legend: {
      enabled: false
    },
    plotOptions: {
      bar: {
        groupPadding: 0.1,
        pointPadding: 0.2,
        borderWidth: 0
      }
    },
    series: [{
      name: 'Benchmark Range',
      data: rangeData,
      type: 'columnrange',
      color: 'rgba(243, 104, 204, 0.16)'
    }, {
      name: 'Actual Count',
      data: actualData,
      type: 'scatter',
      marker: {
        symbol: 'diamond',
        radius: 6,
        fillColor: '#F368CC',
        lineWidth: 2,
        lineColor: '#F368CC'
      }
    }],
    tooltip: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      borderColor: 'transparent',
      style: {
        color: 'white',
        fontSize: '12px'
      },
      formatter: function() {
        if (this.series.name === 'Actual Count') {
          return `
            <div style="color: white;">
              <p><b>${this.point.name}</b>: ${this.point.y} suppliers</p>
              <p>Benchmark Range (Q1–Q3): ${this.point.q1} – ${this.point.q3}</p>
              <p>Median: ${this.point.median}</p>
            </div>
          `;
        }
        return false;
      },
      useHTML: true
    },
    credits: {
      enabled: false
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500
        },
        chartOptions: {
          chart: {
            height: 350
          },
          xAxis: {
            labels: {
              style: {
                fontSize: '9px'
              }
            }
          }
        }
      }]
    }
  };

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
    />
  );
};
  const toggle = (setter) => () => setter((prev) => !prev);

  const SectionHeader = ({ title, expanded, toggleExpanded }) => (
    <Box
      display="flex"
      alignItems="center"
      marginBottom="24px"
      marginLeft="12px"
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{
          color: "white",
          fontStyle: "Roboto",
          fontSize: "16px",
          fontWeight: 700,
        }}
      >
        <GridViewOutlined sx={{ marginRight: "4px", fontSize: "16px" }} />
        {title}
      </Box>
      <Box flexGrow={1} borderBottom="1px solid #FFFFFF29" marginLeft="12px" />
      <IconButton
        onClick={toggleExpanded}
        sx={{ color: "white", padding: 0, marginLeft: "8px" }}
      >
        {expanded ? <ExpandLess /> : <ExpandMore />}
      </IconButton>
    </Box>
  );

  return (
    <div style={{ display: "flex", flexDirection: "row" }}>
      <Box
        sx={{
          backgroundColor: "#000",
          backgroundImage: `url(${bgsd})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          minHeight: "100vh",
          padding: "24px",
          width: "100%",
          marginTop: "60px",
        }}
      >
        <CssBaseline />
        <AppBar
          position="fixed"
          sx={{
            width: "100%",
            height: "65px",
            opacity: 1,
            borderBottom: "1px solid #2C2D3C",
            paddingLeft: "35px",
            bgcolor: "#181921",
          }}
        >
          <Toolbar
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
              paddingRight: "16px",
            }}
          >
            <Typography
              variant="h4"
              noWrap
              component="div"
              style={{
                fontStyle: "Roboto",
                fontSize: "18px",
                fontWeight: 600,
                textAlign: "left",
                // marginLeft: "2rem",
              }}
            >
              <img src={Logo} style={{ width: "159px", height: "40px" }} />
            </Typography>
          </Toolbar>
        </AppBar>
        <Box display="flex" alignItems="center" marginBottom="24px">
          <Box
            display="flex"
            alignItems="center"
            sx={{
              color: "white",
              fontStyle: "Roboto",
              fontSize: "16px",
              fontWeight: 700,
            }}
          >
            <GridViewOutlined sx={{ marginRight: "4px", fontSize: "16px" }} />
            Benchmarks
          </Box>
          <Box
            flexGrow={1}
            borderBottom="1px solid #FFFFFF29"
            marginLeft="12px"
          />
        </Box>

        {/* Company Benchmarks */}
        <Section>
          <SectionHeader
            title="Company Benchmarks"
            expanded={expandedCompany}
            toggleExpanded={toggle(setExpandedCompany)}
          />
          {expandedCompany && (
            <>
              <Box
                display="flex"
                alignItems="center"
                marginBottom="12px"
                marginLeft="24px"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  sx={{
                    color: "white",
                    fontStyle: "Roboto",
                    fontSize: "16px",
                    fontWeight: 600,
                  }}
                >
                  <FaChartLine
                    style={{ marginRight: "6px", fontSize: "16px" }}
                  />
                  Spend as a Percentage of Revenue
                </Box>
                <Box
                  flexGrow={1}
                  borderBottom="1px solid #FFFFFF29"
                  marginLeft="12px"
                />
              </Box>
              <ChartWrapper
                benchmarkName="Client Spend By Revenue"
                chartComponent={
                  <BenchmarkChart benchmarkName="Client Spend By Revenue" />
                }
                showRecommendations={true}
              />
            </>
          )}
        </Section>

        {/* Supplier Benchmarks */}
        <Section>
          <SectionHeader
            title="Supplier Benchmarks"
            expanded={expandedSupplier}
            toggleExpanded={toggle(setExpandedSupplier)}
          />
          {expandedSupplier && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Top 10 Suppliers Contributing to percent of Category Spend
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>

                  <ChartWrapper
                    benchmarkName="Supplier Pareto"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Supplier Pareto" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Suppliers covering top 80% of Spend
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Top 80% Total Suppliers"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Top 80% Total Suppliers" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                    marginTop="6px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Spend by Supplier for Top 80% Suppliers
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Spend by Supplier for top 80% Suppliers"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Spend by Supplier for top 80% Suppliers" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                    marginTop="6px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Spend by Supplier for Bottom 20% Suppliers
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Spend by Supplier for bottom 20% Suppliers"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Spend by Supplier for bottom 20% Suppliers" />
                    }
                    showRecommendations={true}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Top 80% Suppliers By Category
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Top 80% Suppliers By Category"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Top 80% Suppliers By Category" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                    marginTop="6px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Bottom 20% Suppliers By Category
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Bottom 20% Suppliers By Category"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Bottom 20% Suppliers By Category" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
              </Grid>

              <Box sx={{ marginTop: 4 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  marginBottom="12px"
                  marginLeft="24px"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: "white",
                      fontStyle: "Roboto",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    <FaChartLine
                      style={{ marginRight: "6px", fontSize: "16px" }}
                    />
                    Supplier Prevalence of the Category
                  </Box>
                  <Box
                    flexGrow={1}
                    borderBottom="1px solid #FFFFFF29"
                    marginLeft="12px"
                  />
                </Box>
                <ChartWrapper
                  benchmarkName="Supplier Prevalence"
                  chartComponent={<SupplierPrevalenceChart />}
                  showRecommendations={true}
                />
              </Box>

              <Box sx={{ marginTop: 4 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  marginBottom="12px"
                  marginLeft="24px"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: "white",
                      fontStyle: "Roboto",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    <FaChartLine
                      style={{ marginRight: "6px", fontSize: "16px" }}
                    />
                    Supplier Share of Wallet for Top 10 Suppliers
                  </Box>
                  <Box
                    flexGrow={1}
                    borderBottom="1px solid #FFFFFF29"
                    marginLeft="12px"
                  />
                </Box>
                <ChartWrapper
                  benchmarkName="Supplier Wallet Share"
                  chartComponent={
                    <BenchmarkChart benchmarkName="Supplier Wallet Share" />
                  }
                  showRecommendations={true}
                />
              </Box>
            </>
          )}
        </Section>

        {/* Category Benchmarks */}
        <Section>
          <SectionHeader
            title="Category Benchmarks"
            expanded={expandedCategory}
            toggleExpanded={toggle(setExpandedCategory)}
          />
          {expandedCategory && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Category Spend Contribution to Total Spend
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Category Spend By Total Spend"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Category Spend By Total Spend" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box
                    display="flex"
                    alignItems="center"
                    marginBottom="12px"
                    marginLeft="24px"
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      sx={{
                        color: "white",
                        fontStyle: "Roboto",
                        fontSize: "16px",
                        fontWeight: 600,
                      }}
                    >
                      <FaChartLine
                        style={{ marginRight: "6px", fontSize: "16px" }}
                      />
                      Category Spend Contribution to Revenue
                    </Box>
                    <Box
                      flexGrow={1}
                      borderBottom="1px solid #FFFFFF29"
                      marginLeft="12px"
                    />
                  </Box>
                  <ChartWrapper
                    benchmarkName="Category Spend By Revenue"
                    chartComponent={
                      <BenchmarkChart benchmarkName="Category Spend By Revenue" />
                    }
                    showRecommendations={true}
                  />
                </Grid>
              </Grid>

              <Box sx={{ marginTop: 4 }}>
                <Box
                  display="flex"
                  alignItems="center"
                  marginBottom="12px"
                  marginLeft="24px"
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{
                      color: "white",
                      fontStyle: "Roboto",
                      fontSize: "16px",
                      fontWeight: 600,
                    }}
                  >
                    <FaChartLine
                      style={{ marginRight: "6px", fontSize: "16px" }}
                    />
                    Category Fragmentation of Sub Categories
                  </Box>
                  <Box
                    flexGrow={1}
                    borderBottom="1px solid #FFFFFF29"
                    marginLeft="12px"
                  />
                </Box>
                <ChartWrapper
                  benchmarkName="Category Fragmentation"
                  chartComponent={<FragmentationChart />}
                  showRecommendations={true}
                />
              </Box>
            </>
          )}
        </Section>
      </Box>
    </div>
  );
};

export default MetacubeInsights;
